import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';

const selectConfig = (state: RootState) => state.config;
const selectNodeEnv = createSelector(selectConfig, (state) => state.NODE_ENV);
const selectFeatureFlagTaskRouter = createSelector(
  selectConfig,
  (state) => state.FEATUREFLAG_TASKROUTER
);
const selectTaskRouterWaitingTime = createSelector(
  selectConfig,
  (state) => state.TASKROUTER_WAITING_TIME
);
const selectFeatureFlagMenuVersion = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_MENU_VERSION
);
const selectTaskRouterNotificationSound = createSelector(
  selectConfig,
  (state) => state.TASKROUTER_NOTIFICATION_SOUND
);
const selectPagerdutyIntegrationKey = createSelector(
  selectConfig,
  (state) => state.PAGERDUTY_INTEGRATION_KEY
);
const selectSessionBufferAPI = createSelector(
  selectConfig,
  (state) => state.SESSION_BUFFER_API
);
const selectSessionBufferAPIAuthentication = createSelector(
  selectConfig,
  (state) => state.SESSION_BUFFER_API_AUTHENTICATION
);
const selectAuthURL = createSelector(selectConfig, (state) => state.AUTH_URL);
const selectTRAgentAutoAssignment = createSelector(
  selectConfig,
  (state) => state.TASK_ROUTER_AGENT_AUTO_ASSIGNMENT
);
const selectDisableIntentInterval = createSelector(
  selectConfig,
  (state) => state.DISABLE_INTENT_INTERVAL
);

const selectEventAPI = createSelector(selectConfig, (state) => state.EVENT_API);

const selectEventAPIAuth = createSelector(
  selectConfig,
  (state) => state.EVENT_API_BASIC_AUTH_PARAM
);

const selectFeatureFlagAudioWebsocket = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUDIO_BACKEND
);

const selectFinishOrderTimer = createSelector(
  selectConfig,
  (state) => state.FINISH_ORDER_TIMER
);

const selectAgentInactivityTimer = createSelector(
  selectConfig,
  (state) => state.AGENT_INACTIVITY_TIMER
);

const selectFeatureFlagAutoCombo = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUTO_COMBO
);

const selectFeatureFlagBMAutoCombo = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_BM_AUTO_COMBO
);

const selectFeatureFlagLoyaltySupport = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_LOYALTY_SUPPORT
);

const selectAIEscalationUpsell = createSelector(
  selectConfig,
  (state) => state.AI_ESCALATION_UPSELL
);

const selectIdleLogOutTime = createSelector(
  selectConfig,
  (state) => state.IDLE_LOGOUT_TIME
);

const selectFeatureFlagFinalOrderValidation = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_FINAL_ORDER_VALIDATION
);

const selectFeatureFlagPosCart = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_POS_CART
);

const selectFeatureFlagItemConversion = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ITEM_CONVERSION
);

const selectFeatureFlagOCBReadback = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_OCB_READBACK
);

const selectFinalOrderPendingTimeout = createSelector(
  selectConfig,
  (state) => state.FINAL_ORDER_PENDING_TIMEOUT
);

const selectFinalOrderCompletedTimeout = createSelector(
  selectConfig,
  (state) => state.FINAL_ORDER_COMPLETED_TIMEOUT
);

const selectFeatureFlagNotifyHitlBuild = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_NOTIFY_HITL_BUILD
);

const selectFinishOrderButtonDisableInterval = createSelector(
  selectConfig,
  (state) => state.FINISH_ORDER_BUTTON_DISABLE_INTERVAL
);

const selectFeatureFlagSessionEndOrderActionChanges = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SESSION_END_ORDER_ACTION_CHANGES
);

const selectFeatureFlagTimerResetOnCartUpdate = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_TIMER_RESET_ON_CART_UPDATE
);

const selectFeatureFlagAgentCart = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AGENT_CART
);

const selectFeatureFlagSuppressUpsell = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SUPPRESS_UPSELL
);

const selectFeatureFlagDoNotUpsellInExpressMode = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE
);

const selectFeatureFlagUnresolvedItems = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_UNRESOLVED_ITEMS
);

const selectFeatureFlagHitl3 = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_HITL_3_0
);

const selectFeatureFlagHitlExpressTimer = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_HITL_EXPRESS_TIMER
);

const selectHitlExpressTimerMaxSeconds = createSelector(
  selectConfig,
  (state) => state.HITL_EXPRESS_TIMER_MAX_SECONDS
);

const selectAgentResponseTimeLimitSeconds = createSelector(
  selectConfig,
  (state) => state.AGENT_RESPONSE_TIME_LIMIT_SECONDS
);

const selectFeatureFlagMenuModuleErrorUi = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_MENU_MODULE_ERROR_UI
);

/** HITL-1551 kill switch, read at the staff-intervention call site so that with
 * the flag off the intervention takes literally today's path. */
const selectFeatureFlagAutofillFinalOrderOnIntervention = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION
);

/** HITL-1562 kill switch for the auto-submit autofill (post-car_exit timer
 * expiry), read at the call site so that with the flag off it takes literally
 * today's path. Independent of the intervention flag above. */
const selectFeatureFlagAutofillFinalOrderOnAutoSubmit = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT
);

const selectFeatureFlagOrderCheckRetry = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ORDER_CHECK_RETRY
);

const selectOrderCheckTimeoutSeconds = createSelector(
  selectConfig,
  (state) => state.ORDER_CHECK_TIMEOUT_SECONDS
);

const selectFeatureFlagOrderStatusBanner = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ORDER_STATUS_BANNER
);

const selectFeatureFlagSecondaryAsrEnabled = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SECONDARY_ASR_ENABLED
);

const selectConsolidatedMenuBrands = createSelector(
  selectConfig,
  (state) => state.CONSOLIDATED_MENU_BRANDS
);

const selectConsolidatedMenuExcludedRestaurants = createSelector(
  selectConfig,
  (state) => state.CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS
);

const selectFeatureFlagIssueReporting = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ISSUE_REPORTING
);

/**
 * HITL-1492 Pillar 3: the gate every issue-reporting consumer reads.
 *
 * The AND lives here, once, so the four UI tickets that follow cannot get it
 * wrong: the bottom action bar the Issue button lives in only exists in
 * MainPageLayout (hitl_3_0), but the feature stays independently killable.
 */
const selectIsIssueReportingEnabled = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagIssueReporting,
  (isHitl3, isIssueReporting) => Boolean(isHitl3 && isIssueReporting)
);

export {
  selectAgentInactivityTimer,
  selectAuthURL,
  selectConfig,
  selectDisableIntentInterval,
  selectEventAPI,
  selectEventAPIAuth,
  selectFeatureFlagAutoCombo,
  selectFeatureFlagBMAutoCombo,
  selectFeatureFlagLoyaltySupport,
  selectFeatureFlagAudioWebsocket,
  selectFeatureFlagMenuVersion,
  selectFeatureFlagPosCart,
  selectFeatureFlagItemConversion,
  selectFeatureFlagOCBReadback,
  selectFeatureFlagTaskRouter,
  selectFinishOrderTimer,
  selectNodeEnv,
  selectPagerdutyIntegrationKey,
  selectSessionBufferAPI,
  selectSessionBufferAPIAuthentication,
  selectTRAgentAutoAssignment,
  selectTaskRouterNotificationSound,
  selectTaskRouterWaitingTime,
  selectAIEscalationUpsell,
  selectIdleLogOutTime,
  selectFeatureFlagFinalOrderValidation,
  selectFinalOrderPendingTimeout,
  selectFinalOrderCompletedTimeout,
  selectFeatureFlagNotifyHitlBuild,
  selectFinishOrderButtonDisableInterval,
  selectFeatureFlagSessionEndOrderActionChanges,
  selectFeatureFlagTimerResetOnCartUpdate,
  selectFeatureFlagAgentCart,
  selectFeatureFlagSuppressUpsell,
  selectFeatureFlagDoNotUpsellInExpressMode,
  selectFeatureFlagUnresolvedItems,
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectHitlExpressTimerMaxSeconds,
  selectAgentResponseTimeLimitSeconds,
  selectFeatureFlagMenuModuleErrorUi,
  selectFeatureFlagOrderCheckRetry,
  selectOrderCheckTimeoutSeconds,
  selectFeatureFlagOrderStatusBanner,
  selectFeatureFlagSecondaryAsrEnabled,
  selectConsolidatedMenuBrands,
  selectConsolidatedMenuExcludedRestaurants,
  selectFeatureFlagIssueReporting,
  selectIsIssueReportingEnabled,
  selectFeatureFlagAutofillFinalOrderOnIntervention,
  selectFeatureFlagAutofillFinalOrderOnAutoSubmit,
};
